import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { DetailDialog } from '@/shared/components/crud/detail-dialog'
import { FormField } from '@/shared/components/crud/form-field'
import { Input } from '@/shared/components/ui/input'
import { setBalanceSchema, type SetBalanceFormValues } from '../schemas/bank-account.schema'
import { setAccountBalance } from '../api/bank-accounts-api'
import type { BankAccount } from '../types/bank-account'

interface SetBalanceDialogProps {
  account: BankAccount | null
  onOpenChange: (open: boolean) => void
}

const FORM_ID = 'set-balance-form'

export function SetBalanceDialog({ account, onOpenChange }: SetBalanceDialogProps) {
  const open = account !== null

  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: setAccountBalance,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['Accounts', 'list'] })
      toast.success('Balance updated')
      onOpenChange(false)
    },
    onError: () => toast.error('Failed to update balance'),
  })

  const form = useForm<SetBalanceFormValues>({
    resolver: zodResolver(setBalanceSchema),
    defaultValues: { accountId: 0, balanceStartDate: '', balance: 0 },
  })

  useEffect(() => {
    if (account) {
      const yesterday = new Date()
      yesterday.setUTCDate(yesterday.getUTCDate() - 1)
      form.reset({
        accountId: account.id,
        balanceStartDate:
          account.lastTransactionDate?.slice(0, 10) ??
          account.closingBalanceDate?.slice(0, 10) ??
          yesterday.toISOString().slice(0, 10),
        balance: account.closingBalance ?? 0,
      })
    }
  }, [account, form])

  return (
    <DetailDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Set Closing Balance — ${account?.accountName ?? ''}`}
      formId={FORM_ID}
      isSubmitting={mutation.isPending}
      submitLabel="Update Balance"
      maxWidth="sm:max-w-sm"
    >
      <form
        id={FORM_ID}
        onSubmit={form.handleSubmit((v) =>
          mutation.mutate({
            accountId: v.accountId,
            valueDate: v.balanceStartDate,
            balanceValue: v.balance,
          }),
        )}
        className="flex flex-col gap-4"
      >
        <FormField control={form.control} name="balanceStartDate" label="Balance Date" required>
          {(field) => <Input type="date" {...field} />}
        </FormField>
        <FormField control={form.control} name="balance" label="Balance" required>
          {(field) => <Input type="number" step="0.01" {...field} />}
        </FormField>
      </form>
    </DetailDialog>
  )
}
